import { CommissionId } from '@business/entities/commission';
import { NylasResponse } from '@business/entities/nylas';
import { PricingTierResponse } from '@business/entities/order';
import { EscalateResponse } from '@business/entities/tenant';
import { AdminIntegrationResponse } from '../adminIntegration';
import { IntegrationResponse } from '../integration';
import { CompactLabelResponse } from '../label';
import { MBSettingsResponse } from '../marketing';
import { BoardResponse } from '../project';
import { ProjectPriorityResponse } from '../project/ProjectPriorityResponse';
import { SettingFieldResponse } from '../settingFields';
import { COFormData } from '@modules/CompanyOverviewPage/EditCompanyOverviewItems/EditCompanyOverviewModal';
import { DealBoardId } from '../deal';
import { CommissionConfigFormData } from '@modules/settings/SettingsPage/Components/CommissionSettingsTab/CommissionConfigTab';
import { OrgStatusId, OrganizationStatusResponse } from '../organization_status';
import { ContactStatusResponse } from '../contact_status';
import { ProposalSettingsFormData } from '@modules/settings/SettingsPage/Components/BillingTab/Tabs/ProposalSettingsTab';
import { S } from '@fullcalendar/core/internal-common';
import { UserId } from '../account';
import { AffiliateSettingsFormData } from '@modules/settings/SettingsPage/Components/AffiliateTab/AffiliateTab';

export interface AccountSettingsResponse {
  management: boolean;
  orders: boolean;
  proposals: boolean;
  ai: boolean;
  timeMaterials: boolean;
  emailSync: boolean;
  emailSyncNumber: number;
  advancedReporting: boolean;
  advancedAI: boolean;
  assistants: AssistantsMap;
  aiDashboard: boolean;
}

export const DEFAULT_ACCOUNT_SETTINGS: AccountSettingsResponse = {
  management: false,
  orders: false,
  proposals: false,
  ai: false,
  timeMaterials: false,
  emailSync: false,
  advancedReporting: false,
  emailSyncNumber: 0,
  advancedAI: false,
  assistants: {},
  aiDashboard: false,
};

export interface AssistantsMap {
  [assistantId: string]: boolean;
}

export interface SchedularSettingResponse {
  isGCSchedularEnabled: boolean;
}

export interface SettingsResponse {
  appId: string;
  yek: string;
  appName: string;
  manager: {
    id: UserId;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    avatar?: string;
  } | null;
  customFields: SettingFieldResponse[];
  integrations: IntegrationResponse[];
  boards: BoardResponse[];
  priorites: ProjectPriorityResponse[];
  nylas: NylasResponse;
  escalate: EscalateResponse;
  invoiceClassRequired: boolean;
  invoiceClassEnabled: boolean;
  defaultSendInvoice: boolean;
  isSalesTax: boolean;
  appLabels: CompactLabelResponse[];
  MBSettings: MBSettingsResponse;
  numOfBrands: number;
  showVendasta: boolean;
  isGemsOne: boolean;
  isMulticurrency: boolean;
  allowContactAddress: boolean;

  paymentReceiveNotification: boolean;
  receiveNotificationEmails: string[];

  failureNotificationEmails: string[];
  commissionSplitNotificationEmails: string[];
  failureNotificationType: string;
  paymentFailureNotification: boolean;

  statsSettings: COFormData;
  statsEmails: string[];
  adminIntegrations: AdminIntegrationResponse[];
  accountSettings: AccountSettingsResponse;
  isExternalAccount: boolean;
  externalAccountId: string;
  externalAccountUrl: string;
  order: OrderSettingResponse;
  tm: TMSettingResponse;
  deal: DealSettingResponse;
  focusMode: FocusModeSettingsResponse;
  pricingTiers: PricingTierResponse[];
  hasStores: boolean;
  commissionEnabled: boolean;

  commission: CommissionConfigFormData;

  commissionErrors: {
    product: string[];
    commission: {
      id: CommissionId;
      error: string;
    }[];
  };

  orgStatuses: OrganizationStatusResponse[];
  contactStatuses: ContactStatusResponse[];

  proposalSettings: ProposalSettingsFormData;
  defaultIntegration: string;
  isVat: boolean;
  clockinEnabled: boolean;
  reportUsers: UserId[];
  instructors: UserId[];

  isCreditCardProcessingFee: boolean;
  creditCardProcessingFee: number;
  processingFeeService: string;

  project: projectSettingResponse;
  support: SupportSettingResponse;
  autoTags: AutoTagsSettingResponse;
  afterPurchaseStatus: FromToOrgStatusResponse;
  noActiveProjectStatus: FromToOrgStatusResponse;
  doNotChangeStatus: OrgStatusId[];

  dailyUserReport: boolean;
  weeklyUserReport: boolean;
  tmFlag: BoardTmSettingResponse;
  ptoEnabled: boolean;

  affiliate?: AffiliateSettingsFormData;
  applyCourseChangesToCompleted: boolean;
  applyCourseChangesToEnrollers: boolean;

  splits: { userId: string; percentage: number }[];

  schedular: SchedularSettingResponse;
  isGoogleMapsEnabled: boolean;
}

export interface FromToOrgStatusResponse {
  from: OrgStatusId[];
  to: OrgStatusId;
}

export interface AutoTagsSettingResponse {
  tags: string[];
  isOrgBrandActive: boolean;
}

export interface SupportSettingResponse {
  isSupportTimeTracking: boolean;
  supportAutomaticTrackTime: boolean;
}
export interface projectSettingResponse {
  isProjectTimeTracking: boolean;
  projectAutomaticTrackTime: boolean;
}

/** One priority rule's admin-controlled configuration. */
export interface FocusModeRuleSetting {
  enabled: boolean;
  weight: number;
  /** Rule-specific options (capDays, horizonDays, fallbackDays, baseline). */
  [option: string]: number | boolean;
}

export interface FocusModeSettingsResponse {
  /** Bumped on every save; invalidates every rep's cached queue. */
  version: number;
  /** Off until an admin turns it on. Hides the nav entry and blocks the endpoints. */
  enabled: boolean;
  scope: 'own' | 'all';
  boardIds: string[];
  excludedStageIds: string[];
  maxQueueSize: number;
  minDealValue: number;
  leadAgeDays: number;
  actionMinutes: { call: number; email: number; task: number; review: number };
  rules: { [key: string]: FocusModeRuleSetting };
  /** Rule keys in admin-defined priority order; earlier wins an equal score. */
  ruleOrder: string[];
  tieBreak: string[];
  prospecting: FocusModeProspectingSetting;
}

export interface FocusModeProspectingSetting {
  enabled: boolean;
  recycleWindowDays: number;
  dormancyThresholdDays: number;
  maxPerSection: number;
}

/**
 * The settings screen saves one section at a time, so every field is optional and the
 * API merges the payload over what is stored. version is server-managed.
 */
export type FocusModeSettingsFormData = Partial<Omit<FocusModeSettingsResponse, 'version'>>;

export interface DealSettingResponse {
  successDays: number;
  warningDays: number;
  dangerDays: number;
  allDealBoards?: boolean;
  dealBoardId: DealBoardId | null;
}

export interface BoardTmSettingResponse {
  successDays: number;
  warningDays: number;
  dangerDays: number;
  allTMBoards?: boolean;
  tmBoardId: DealBoardId | null;
}

export interface OrderSettingResponse {
  estimateSuccessMsg: string;
  estimateText: string;
  hasStores: boolean;
  orderPrefix: string;
  address: string;
  addressLineTwo: string | null;
  city: string;
  state: string;
  postal: string;
  country: string;
  isSalesTax: boolean;
  poNumberSameAsEstimate: boolean;
  isNameEnabled: boolean;
  isUMEnabled: boolean;
  isInventory: boolean;
  unitOfMeasures: {
    title: string;
  }[];
}

export interface TMSettingResponse {
  estimateSuccessMsg: string;
  estimateText: string;
  hasStores: boolean;
  orderPrefix: string;
  address: string;
  addressLineTwo: string | null;
  city: string;
  state: string;
  postal: string;
  country: string;
  isSalesTax: boolean;
  defaultProductSalesTax: boolean;
  poNumberSameAsEstimate: boolean;
  isNameEnabled: boolean;
  isUMEnabled: boolean;
  isInventory: boolean;
  unitOfMeasures: {
    title: string;
  }[];
  form: string;
  isTimeTracking: boolean;
  automaticTrackTime: boolean;
  allowEditPos: boolean;
  isCustomerServiceRepEnabled: boolean;
}

export interface SiteCacheResponse<T> {
  value?: T;
}
